import React, { useState, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Checkbox,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material'
import {
  Warning as WarningIcon,
  FileDownload as ExportIcon,
  CheckCircle as MarkHandledIcon,
} from '@mui/icons-material'
import { useList, useUpdate, useNotification } from '@refinedev/core'
import { format } from 'date-fns'

interface ExpiredDiscount {
  org_uuid: string
  org_name: string
  discount_code: string
  original_amount: number
  discount_amount: number
  final_amount: number
  applied_at: string
  expires_at: string
  expired: boolean
}

export default function ExpiredDiscountsView() {
  const { open } = useNotification()
  const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set())
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  // Fetch organisations with expired discounts
  const { data, isLoading, refetch } = useList<ExpiredDiscount>({
    resource: 'discount-codes/expired-orgs',
    pagination: { pageSize: 100 },
  })

  const expiredDiscounts = data?.data || []

  // Update discount status
  const { mutate: markAsHandled } = useUpdate()

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedOrgs(new Set(expiredDiscounts.map((d) => d.org_uuid)))
      } else {
        setSelectedOrgs(new Set())
      }
    },
    [expiredDiscounts]
  )

  const handleSelectOne = useCallback((orgUuid: string) => {
    setSelectedOrgs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(orgUuid)) {
        newSet.delete(orgUuid)
      } else {
        newSet.add(orgUuid)
      }
      return newSet
    })
  }, [])

  const handleMarkAsHandled = useCallback(async () => {
    try {
      // Mark each selected organisation's discount as handled
      await Promise.all(
        Array.from(selectedOrgs).map((orgUuid) =>
          markAsHandled({
            resource: `discount-codes/mark-handled/${orgUuid}`,
            id: orgUuid,
            values: {},
          })
        )
      )

      open?.({
        type: 'success',
        message: `Marked ${selectedOrgs.size} organisations as handled`,
      })
      setSelectedOrgs(new Set())
      setConfirmDialogOpen(false)
      refetch()
    } catch {
      open?.({
        type: 'error',
        message: 'Failed to mark organisations as handled',
      })
    }
  }, [selectedOrgs, markAsHandled, open, refetch])

  const handleExport = useCallback(() => {
    // Create CSV content
    const headers = [
      'Organisation Name',
      'Organisation UUID',
      'Discount Code',
      'Original Amount',
      'Discount Amount',
      'Final Amount',
      'Applied Date',
      'Expired Date',
    ]

    const rows = expiredDiscounts
      .filter((d) => selectedOrgs.size === 0 || selectedOrgs.has(d.org_uuid))
      .map((d) => [
        d.org_name,
        d.org_uuid,
        d.discount_code,
        d.original_amount.toFixed(2),
        d.discount_amount.toFixed(2),
        d.final_amount.toFixed(2),
        format(new Date(d.applied_at), 'yyyy-MM-dd'),
        format(new Date(d.expires_at), 'yyyy-MM-dd'),
      ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `expired-discounts-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)

    open?.({
      type: 'success',
      message: 'Export completed',
    })
  }, [expiredDiscounts, selectedOrgs, open])

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <WarningIcon color="warning" />
              <Typography variant="h5" fontWeight={600}>
                Expired Discounts
              </Typography>
              <Chip
                label={`${expiredDiscounts.length} organisations`}
                color="warning"
                size="small"
              />
            </Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                disabled={expiredDiscounts.length === 0}
              >
                Export{' '}
                {selectedOrgs.size > 0 ? `(${selectedOrgs.size})` : 'All'}
              </Button>
              <Button
                variant="contained"
                startIcon={<MarkHandledIcon />}
                onClick={() => setConfirmDialogOpen(true)}
                disabled={selectedOrgs.size === 0}
                color="success"
              >
                Mark as Handled ({selectedOrgs.size})
              </Button>
            </Box>
          </Box>

          {/* Info Alert */}
          <Alert severity="info" sx={{ mb: 3 }}>
            These organisations have discounts that have expired. Review and
            take appropriate action such as updating their subscription pricing
            or contacting them about renewal.
          </Alert>

          {/* Expired Discounts Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedOrgs.size > 0 &&
                        selectedOrgs.size < expiredDiscounts.length
                      }
                      checked={
                        expiredDiscounts.length > 0 &&
                        selectedOrgs.size === expiredDiscounts.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Discount Code</TableCell>
                  <TableCell align="right">Original Price</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Discounted Price</TableCell>
                  <TableCell>Applied</TableCell>
                  <TableCell>Expired</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiredDiscounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No expired discounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  expiredDiscounts.map((discount) => (
                    <TableRow key={discount.org_uuid}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOrgs.has(discount.org_uuid)}
                          onChange={() => handleSelectOne(discount.org_uuid)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {discount.org_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {discount.org_uuid}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={discount.discount_code}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        ${discount.original_amount.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error">
                          -${discount.discount_amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          ${discount.final_amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(discount.applied_at), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="warning.main">
                          {format(new Date(discount.expires_at), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary */}
          {expiredDiscounts.length > 0 && (
            <Box mt={3}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Total Revenue Impact:</strong> $
                  {expiredDiscounts
                    .reduce((sum, d) => sum + d.discount_amount, 0)
                    .toFixed(2)}{' '}
                  in expired discounts across {expiredDiscounts.length}{' '}
                  organisations
                </Typography>
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Mark Organisations as Handled?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark {selectedOrgs.size} organisation(s) as having their
            expired discounts handled. This action helps track which
            organisations have been contacted or had their pricing updated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMarkAsHandled}
            variant="contained"
            color="success"
          >
            Mark as Handled
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
